import { queryOptions } from '@app/configs/query';

import { DEFAULT_SELECT_PAGE_SIZE } from '@shared/constants/selectPagination';
import type {
    DefaultOptionType,
    PaginatedResponse,
    ResponseType,
} from '@shared/types/types';

import { ProcessFieldSelectFieldAiChat } from '../ProcessFieldSelect/ProcessFieldSelectFieldAiChat';

import { ShadcnButton } from '@shadcn-features/core';
import type { ShadcnFormMultiSelectPaginationQuery } from '@shadcn-features/core/ShadcnForm/fields/ShadcnFormMultiSelectPagination';
import { useShadcnInfiniteQuery } from '@shadcn-features/query';
import { LoaderCircle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = Omit<
    React.ComponentProps<typeof ProcessFieldSelectFieldAiChat>,
    'query'
> & {
    query: ShadcnFormMultiSelectPaginationQuery;
};

export function ProcessFieldSelectServiceFieldAiChat({
    query,
    showSearch = true,
    ...props
}: Props) {
    const { t } = useTranslation();
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const timeout = window.setTimeout(
            () => setDebouncedSearch(showSearch ? search.trim() : ''),
            search ? 600 : 0,
        );
        return () => window.clearTimeout(timeout);
    }, [search, showSearch]);

    const infiniteQuery = useMemo(
        () =>
            ({
                ...query,
                params: (page = 1) => ({
                    ...(typeof query.params === 'function'
                        ? query.params(page)
                        : query.params),
                    ...(debouncedSearch ? { text: debouncedSearch } : {}),
                    page,
                    size: DEFAULT_SELECT_PAGE_SIZE,
                }),
                queryOptions: {
                    ...query.queryOptions,
                    initialPageParam: 1,
                    getNextPageParam: (lastPage, pages) => {
                        const loadedCount = pages.reduce(
                            (total, page) =>
                                total + (page.itemList?.length ?? 0),
                            0,
                        );
                        return lastPage.itemList?.length &&
                            loadedCount < (lastPage.itemCount ?? 0)
                            ? pages.length + 1
                            : undefined;
                    },
                },
            }) satisfies ShadcnFormMultiSelectPaginationQuery,
        [query, debouncedSearch],
    );

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        isError,
        refetch,
    } = useShadcnInfiniteQuery<
        ResponseType<PaginatedResponse<DefaultOptionType>>,
        Record<string, unknown>,
        unknown,
        PaginatedResponse<DefaultOptionType>
    >(queryOptions, infiniteQuery);
    const options = useMemo(() => {
        const unique = new Map<string | number, DefaultOptionType>();
        for (const page of data?.pages ?? []) {
            for (const option of page.itemList ?? [])
                unique.set(
                    option.value,
                    props.field.options.mode === 'multiple'
                        ? { ...option, value: String(option.value) }
                        : option,
                );
        }
        return [...unique.values()];
    }, [data?.pages, props.field.options.mode]);
    const searching = showSearch && search.trim() !== debouncedSearch;
    const loading = query.enabled !== false && (isLoading || searching);

    const status = isError ? (
        <div
            role='alert'
            className='flex items-center justify-between gap-3 px-2.5 py-3 text-sm text-(--color-danger)'
        >
            {t('translation:error')}
            <ShadcnButton
                type='button'
                size='sm'
                variant='outline'
                onClick={() => void refetch()}
            >
                {t('translation:refresh')}
            </ShadcnButton>
        </div>
    ) : loading || isFetchingNextPage ? (
        <div
            role='status'
            aria-label={t('translation:loading')}
            className='flex justify-center py-3'
        >
            <LoaderCircle
                aria-hidden
                className='size-4 animate-spin text-(--color-card-text-secondary)'
            />
        </div>
    ) : hasNextPage ? (
        <ShadcnButton
            type='button'
            size='sm'
            variant='ghost'
            className='w-full'
            onClick={() => void fetchNextPage()}
        >
            {t('translation:showMore')}
        </ShadcnButton>
    ) : null;

    return (
        <ProcessFieldSelectFieldAiChat
            {...props}
            showSearch={showSearch}
            options={searching ? [] : options}
            searchValue={search}
            onSearchChange={setSearch}
            filterOptionsLocally={false}
            emptyState={loading || isError ? <></> : undefined}
            listFooter={status}
            onListScroll={(event) => {
                const list = event.currentTarget;
                if (
                    list.scrollHeight - list.scrollTop - list.clientHeight <
                        80 &&
                    hasNextPage &&
                    !isFetchingNextPage &&
                    !loading &&
                    !isError
                )
                    void fetchNextPage();
            }}
        />
    );
}
